/**
 * Segmentation helpers for the Built Index funnel chart.
 *
 * Raw `built_index_events` rows carry a `user_agent` string and an
 * `attribution` blob (utm_* / ft_utm_* / referrer). These helpers normalize
 * those into three drill-down dimensions — device, referrer, campaign — and
 * build unique-session funnels for any subset of events.
 */
import {
  BUILT_INDEX_STEPS,
  BUILT_INDEX_STEP_LABELS,
  type BuiltIndexStep,
} from "@/lib/built-index-analytics";

export type FunnelEvent = {
  id: string;
  occurred_at: string;
  step: string;
  email?: string | null;
  session_id?: string | null;
  path?: string | null;
  user_agent?: string | null;
  attribution?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
};

export const FUNNEL_DIMENSIONS = [
  { key: "device", label: "Device" },
  { key: "referrer", label: "Referrer" },
  { key: "campaign", label: "Campaign" },
] as const;

export type FunnelDimension = (typeof FUNNEL_DIMENSIONS)[number]["key"];

const UNKNOWN = "Unknown";

/** Coarse device class from a user-agent string. */
export function deviceOf(userAgent?: string | null): string {
  const ua = (userAgent ?? "").toLowerCase();
  if (!ua) return UNKNOWN;
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/.test(ua)) return "Tablet";
  if (/mobi|iphone|ipod|android|blackberry|windows phone/.test(ua)) return "Mobile";
  if (/bot|crawler|spider|headless/.test(ua)) return "Bot";
  return "Desktop";
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0].replace(/^www\./, "");
  }
}

/** Referrer source: explicit utm_source wins, else the referring hostname. */
export function referrerOf(attribution?: Record<string, unknown> | null): string {
  const a = attribution ?? {};
  const ref = str(a.referrer) || str(a.ft_referrer);
  if (ref) {
    const host = hostOf(ref);
    if (host) return host;
  }
  const source = str(a.utm_source) || str(a.ft_utm_source);
  if (source) return source.toLowerCase();
  if (str(a.gclid)) return "google ads";
  if (str(a.fbclid)) return "facebook ads";
  return "Direct";
}

/** Campaign name, falling back to first-touch, then medium, then Unknown. */
export function campaignOf(attribution?: Record<string, unknown> | null): string {
  const a = attribution ?? {};
  const campaign = str(a.utm_campaign) || str(a.ft_utm_campaign);
  if (campaign) return campaign.toLowerCase();
  const medium = str(a.utm_medium) || str(a.ft_utm_medium);
  if (medium) return `(${medium.toLowerCase()})`;
  return "(none)";
}

export function segmentOf(event: FunnelEvent, dimension: FunnelDimension): string {
  if (dimension === "device") return deviceOf(event.user_agent);
  if (dimension === "referrer") return referrerOf(event.attribution);
  return campaignOf(event.attribution);
}

/** Stable identity for "one person" in the funnel. */
export function subjectOf(event: FunnelEvent): string {
  return event.session_id ?? event.email ?? event.id;
}

export type FunnelStepStat = {
  step: BuiltIndexStep;
  label: string;
  unique: number;
  total: number;
  /** Conversion from the previous non-empty step, null for the entry step. */
  stepRate: number | null;
  /** Share of the widest step at the top of the funnel. */
  overall: number;
  /** Unique subjects lost versus the previous non-empty step. */
  dropOff: number;
  dropOffRate: number | null;
};

function pct(n: number, d: number): number {
  return d === 0 ? 0 : Math.round((n / d) * 1000) / 10;
}

/** Unique-subject funnel across the provided events. */
export function buildFunnel(events: FunnelEvent[]): FunnelStepStat[] {
  const uniques = new Map<string, Set<string>>();
  const totals = new Map<string, number>();
  for (const ev of events) {
    const set = uniques.get(ev.step) ?? new Set<string>();
    set.add(subjectOf(ev));
    uniques.set(ev.step, set);
    totals.set(ev.step, (totals.get(ev.step) ?? 0) + 1);
  }

  const counts = BUILT_INDEX_STEPS.map((step) => ({
    step,
    label: BUILT_INDEX_STEP_LABELS[step],
    unique: uniques.get(step)?.size ?? 0,
    total: totals.get(step) ?? 0,
  }));

  const top = counts[0]?.unique || counts.find((c) => c.unique > 0)?.unique || 0;
  let prev = 0;
  return counts.map((c) => {
    const stepRate = prev === 0 ? null : pct(c.unique, prev);
    const dropOff = prev === 0 ? 0 : Math.max(0, prev - c.unique);
    const dropOffRate = prev === 0 ? null : pct(dropOff, prev);
    const overall = pct(c.unique, top);
    prev = c.unique || prev;
    return { ...c, stepRate, overall, dropOff, dropOffRate };
  });
}

export type SegmentBreakdown = {
  segment: string;
  entered: number;
  reached: number;
  conversion: number;
  dropOff: number;
};

/**
 * For a given step, break the *previous* step's audience down by dimension and
 * show how many of them reached this step (and how many were lost).
 */
export function breakdownForStep(
  events: FunnelEvent[],
  step: BuiltIndexStep,
  dimension: FunnelDimension,
): SegmentBreakdown[] {
  const index = BUILT_INDEX_STEPS.indexOf(step);
  const stepSubjects = subjectSegments(events, dimension); // subject -> segment
  const reached = new Set<string>();

  // Entry population = subjects who hit the previous non-empty step, or all
  // subjects at the very top of the funnel.
  const subjectsAt = (s: string) => {
    const set = new Set<string>();
    for (const ev of events) if (ev.step === s) set.add(subjectOf(ev));
    return set;
  };

  let entry: Set<string> | null = null;
  for (let i = index - 1; i >= 0; i -= 1) {
    const set = subjectsAt(BUILT_INDEX_STEPS[i]);
    if (set.size > 0) {
      entry = set;
      break;
    }
  }
  if (!entry) entry = new Set(stepSubjects.keys());

  for (const s of subjectsAt(step)) reached.add(s);

  const rows = new Map<string, { entered: number; reached: number }>();
  for (const subject of entry) {
    const segment = stepSubjects.get(subject) ?? UNKNOWN;
    const row = rows.get(segment) ?? { entered: 0, reached: 0 };
    row.entered += 1;
    if (reached.has(subject)) row.reached += 1;
    rows.set(segment, row);
  }
  // Subjects who reached the step without appearing upstream still count.
  for (const subject of reached) {
    if (entry.has(subject)) continue;
    const segment = stepSubjects.get(subject) ?? UNKNOWN;
    const row = rows.get(segment) ?? { entered: 0, reached: 0 };
    row.entered += 1;
    row.reached += 1;
    rows.set(segment, row);
  }

  return Array.from(rows.entries())
    .map(([segment, r]) => ({
      segment,
      entered: r.entered,
      reached: r.reached,
      conversion: pct(r.reached, r.entered),
      dropOff: Math.max(0, r.entered - r.reached),
    }))
    .sort((a, b) => b.entered - a.entered || a.segment.localeCompare(b.segment));
}

/** Distinct segment values present in the data, most common first. */
export function segmentValues(events: FunnelEvent[], dimension: FunnelDimension): string[] {
  const counts = new Map<string, number>();
  for (const ev of events) {
    const seg = segmentOf(ev, dimension);
    counts.set(seg, (counts.get(seg) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([seg]) => seg);
}

/**
 * Segment each subject once, from their earliest event — later events often
 * lose the original campaign/referrer, so a per-event match would under-count.
 */
export function subjectSegments(
  events: FunnelEvent[],
  dimension: FunnelDimension,
): Map<string, string> {
  const sorted = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  const map = new Map<string, string>();
  for (const ev of sorted) {
    const subject = subjectOf(ev);
    const seg = segmentOf(ev, dimension);
    const current = map.get(subject);
    if (current === undefined || current === UNKNOWN || current === "Direct" || current === "(none)") {
      if (current === undefined || (seg !== UNKNOWN && seg !== "Direct" && seg !== "(none)")) {
        map.set(subject, seg);
      }
    }
  }
  return map;
}

export function filterBySegment(
  events: FunnelEvent[],
  dimension: FunnelDimension,
  segment: string | null,
): FunnelEvent[] {
  if (!segment) return events;
  const segments = subjectSegments(events, dimension);
  return events.filter((ev) => segments.get(subjectOf(ev)) === segment);
}
